import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, Clock, Search, Filter, ArrowRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const BlogPage = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { value: 'all', label: 'All Posts' },
    { value: 'AI & Technology', label: 'AI & Technology' },
    { value: 'Strategy', label: 'Strategy' },
    { value: 'Operations', label: 'Operations' },
    { value: 'Case Studies', label: 'Case Studies' }
  ];

  // Fetch blog posts from backend
  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${BACKEND_URL}/api/blog/posts`);
        
        if (response.ok) {
          const data = await response.json();
          setBlogPosts(data);
        } else {
          // Fallback to mock data if API fails
          const { mockData } = await import('../mock/data');
          setBlogPosts(mockData.blogPosts);
        }
      } catch (err) {
        console.error('Error fetching blog posts:', err);
        // Fallback to mock data
        const { mockData } = await import('../mock/data');
        setBlogPosts(mockData.blogPosts);
        setError('Unable to load latest posts. Showing sample content.');
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPosts();
  }, []);

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen py-20">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[var(--accent-primary)] mx-auto mb-4"></div>
            <p className="text-[var(--text-secondary)]">Loading blog posts...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-20">
      <div className="container">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h1 className="display-md text-[var(--text-primary)] mb-6">
            Marqi Insights & Resources
          </h1>
          <p className="body-lg">
            Stay ahead of the curve with expert insights on digital advertising, 
            AI optimization, and performance marketing strategies.
          </p>
        </div>

        {/* Search and Filter */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--text-muted)] w-5 h-5" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-[var(--bg-secondary)] border-[var(--border-primary)] text-[var(--text-primary)] focus:border-[var(--accent-primary)]"
              />
            </div>
            
            <div className="flex gap-2 flex-wrap">
              {categories.map((category) => (
                <Button
                  key={category.value}
                  variant={selectedCategory === category.value ? "default" : "outline"}
                  onClick={() => setSelectedCategory(category.value)}
                  className={`text-sm ${
                    selectedCategory === category.value
                      ? 'bg-[var(--accent-primary)] text-[var(--bg-primary)] hover:bg-[var(--accent-hover)]'
                      : 'border-[var(--border-primary)] text-[var(--text-secondary)] hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)]'
                  }`}
                >
                  {category.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Results Count */}
          <div className="text-[var(--text-muted)] mb-8">
            Showing {filteredPosts.length} of {blogPosts.length} articles
          </div>
        </div>

        {/* Featured Post */}
        {filteredPosts.length > 0 && (
          <div className="max-w-6xl mx-auto mb-16">
            <div className="feature-card overflow-hidden">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="aspect-[16/10] bg-[var(--bg-tertiary)] rounded-lg overflow-hidden">
                  <img
                    src={filteredPosts[0].image}
                    alt={filteredPosts[0].title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                
                <div className="flex flex-col justify-center">
                  <div className="inline-flex items-center px-3 py-1 bg-[var(--accent-bg)] rounded-full text-[var(--accent-primary)] text-sm font-medium mb-4 self-start">
                    Featured Article
                  </div>
                  
                  <h2 className="h1 text-[var(--text-primary)] mb-4 hover:text-[var(--accent-primary)] transition-colors cursor-pointer">
                    {filteredPosts[0].title}
                  </h2>
                  
                  <p className="body-md mb-6">
                    {filteredPosts[0].excerpt}
                  </p>
                  
                  <div className="flex items-center space-x-6 text-sm text-[var(--text-muted)] mb-6">
                    <div className="flex items-center">
                      <User className="w-4 h-4 mr-2" />
                      {filteredPosts[0].author}
                    </div>
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      {formatDate(filteredPosts[0].published_at || filteredPosts[0].created_at)}
                    </div>
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-2" />
                      {filteredPosts[0].readTime}
                    </div>
                  </div>
                  
                  <Link to={`/blog/${filteredPosts[0].slug}`} className="btn-primary self-start inline-flex items-center px-6 py-3 rounded-lg text-[var(--bg-primary)] font-medium hover:bg-[var(--accent-hover)] transition-colors">
                    Read Full Article
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Blog Grid */}
        <div className="max-w-6xl mx-auto">
          {filteredPosts.length > 0 ? (
            <div className="card-grid">
              {filteredPosts.slice(1).map((post, index) => (
                <article key={post.id} className={`feature-card hover-lift animate-fade-in`} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="aspect-[16/10] bg-[var(--bg-tertiary)] rounded-lg overflow-hidden mb-6">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  
                  <div className="inline-flex items-center px-3 py-1 bg-[var(--accent-bg)] rounded-full text-[var(--accent-primary)] text-sm font-medium mb-4">
                    {post.category}
                  </div>
                  
                  <h3 className="h2 text-[var(--text-primary)] mb-3 hover:text-[var(--accent-primary)] transition-colors cursor-pointer">
                    {post.title}
                  </h3>
                  
                  <p className="body-md mb-6 line-clamp-3">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex items-center space-x-4 text-sm text-[var(--text-muted)] mb-6">
                    <div className="flex items-center">
                      <User className="w-4 h-4 mr-2" />
                      {post.author}
                    </div>
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      {formatDate(post.published_at || post.created_at)}
                    </div>
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-2" />
                      {post.readTime}
                    </div>
                  </div>
                  
                  <Link to={`/blog/${post.slug}`} className="w-full btn-secondary inline-flex items-center justify-center px-4 py-2 rounded-lg border border-[var(--border-primary)] text-[var(--text-primary)] hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)] transition-colors">
                    Read More
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-[var(--bg-secondary)] rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="w-10 h-10 text-[var(--text-muted)]" />
              </div>
              <h3 className="h2 text-[var(--text-primary)] mb-4">No articles found</h3>
              <p className="body-md">
                Try adjusting your search terms or browse all categories.
              </p>
            </div>
          )}
        </div>

        {/* Newsletter Signup */}
        <section className="max-w-4xl mx-auto mt-24">
          <div className="feature-card text-center">
            <h2 className="h1 text-[var(--text-primary)] mb-4">
              Stay Updated with Marqi Insights
            </h2>
            <p className="body-md mb-8">
              Get the latest digital advertising strategies, AI optimization tips, 
              and industry insights delivered to your inbox.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                className="flex-1 bg-[var(--bg-secondary)] border-[var(--border-primary)] text-[var(--text-primary)] focus:border-[var(--accent-primary)]"
              />
              <Button className="btn-primary">
                Subscribe
              </Button>
            </div>
            
            <p className="text-sm text-[var(--text-muted)] mt-4">
              No spam. Unsubscribe anytime. We respect your privacy.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default BlogPage;